// Kicksociety collection grid: AJAX navigation (Section Rendering API, section_id=ks-collection) with shareable URLs,
// working back/forward, staggered card reveal, mobile filter sheet, and "My size" auto-apply.
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const SID = 'ks-collection';

  function reveal(root) {
    const cards = [...root.querySelectorAll('.ks-card:not(.is-in)')];
    if (reduced || !('IntersectionObserver' in window)) { cards.forEach(c => c.classList.add('is-in')); return; }
    const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -5% 0px' });
    cards.forEach(c => io.observe(c));
    setTimeout(() => cards.forEach(c => { if (c.getBoundingClientRect().top < innerHeight) c.classList.add('is-in'); }), 1200);  // safety net
  }

  // the control that had focus, described so its counterpart can be found in the re-rendered section
  const GROUPS = '.ks-seg, .ks-chips, .ks-pager, .ks-rail, .ks-empty__ctas, .ks-filters__row';
  const labelOf = el => (el.dataset.size || el.childNodes[0]?.textContent || el.textContent || '').trim();
  function focusKey(plp) {
    const el = document.activeElement;
    if (!el || !plp.contains(el) || el === plp) return null;
    const g = el.closest(GROUPS);
    return { group: g ? GROUPS.split(', ').find(sel => g.matches(sel)) : null, label: labelOf(el) };
  }
  function restoreFocus(next, key) {
    if (!key) return;
    const pool = key.group ? [...next.querySelectorAll(`${key.group} a[href], ${key.group} button`)] : [];
    const el = pool.find(x => labelOf(x) === key.label) || next.querySelector('.ks-plp__title');
    el?.focus({ preventScroll: true });
  }

  async function load(url, push) {
    const plp = document.querySelector('[data-ks-plp]');
    if (!plp) { location.href = url; return; }
    const key = focusKey(plp);
    plp.classList.add('is-loading');
    try {
      const u = new URL(url, location.origin);
      u.searchParams.set('section_id', SID);
      const res = await fetch(u);
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = doc.querySelector('[data-ks-plp]');
      if (!next) throw new Error('no section');
      const sheetOpen = plp.querySelector('[data-ks-filters]')?.classList.contains('is-sheet');
      setInert(plp, false);
      plp.replaceWith(next);
      if (push) history.pushState({ ks: 1 }, '', url);
      if (next.dataset.docTitle) document.title = next.dataset.docTitle;
      wire(next, sheetOpen);
      restoreFocus(next, key);
      const live = next.querySelector('[data-ks-live]'), count = next.querySelector('[data-ks-count]');
      if (live && count) setTimeout(() => (live.textContent = count.textContent), 30);
      if (!sheetOpen) next.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    } catch (e) { location.href = url; }
  }

  function wire(plp, sheetOpen) {
    reveal(plp);
    plp.querySelectorAll('a[data-ks-nav]').forEach(a => a.addEventListener('click', e => {
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      if (a.hasAttribute('data-ks-clear')) { try { sessionStorage.setItem('ks.noAuto', '1'); } catch {} }
      load(a.href, true);
    }));
    // mobile bottom sheet: modal while open (Escape / backdrop / close button), page behind is inert
    const sheet = plp.querySelector('[data-ks-filters]'), open = plp.querySelector('[data-ks-filter-open]'), close = plp.querySelector('[data-ks-filter-close]');
    if (sheet && open) {
      const set = v => {
        sheet.classList.toggle('is-sheet', v);
        plp.classList.toggle('has-sheet', v);
        if (v) { sheet.setAttribute('role', 'dialog'); sheet.setAttribute('aria-modal', 'true'); }
        else { sheet.setAttribute('role', 'region'); sheet.removeAttribute('aria-modal'); }
        setInert(plp, v);
      };
      if (sheetOpen && matchMedia('(max-width: 767px)').matches) set(true);
      open.addEventListener('click', () => { set(true); close?.focus(); });
      close?.addEventListener('click', () => { set(false); open.focus(); });
      sheet.addEventListener('keydown', e => { if (e.key === 'Escape' && sheet.classList.contains('is-sheet')) { e.stopPropagation(); set(false); open.focus(); } });
      plp.addEventListener('click', e => { if (e.target === plp && plp.classList.contains('has-sheet')) { set(false); open.focus(); } });
    }
  }
  // everything outside the open sheet (header, rail, grid, footer) leaves the tab order and the accessibility tree
  function setInert(plp, on) {
    const sheet = plp.querySelector('[data-ks-filters]');
    const keep = new Set();
    for (let n = sheet; n && n !== document.body; n = n.parentElement) keep.add(n);
    const walk = parent => [...parent.children].forEach(ch => {
      if (ch === sheet || ch.tagName === 'SCRIPT') return;
      if (keep.has(ch)) walk(ch); else if (on) { if (!ch.inert) { ch.inert = true; ch.dataset.ksInert = 1; } } else if (ch.dataset.ksInert) { ch.inert = false; delete ch.dataset.ksInert; }
    });
    if (sheet) walk(document.body);
  }

  // My size: on a plain collection page, jump to the saved size unless the shopper cleared it this session
  function autoApply(plp) {
    if (plp.dataset.mode !== 'col') return false;
    let ms = null, no = false;
    try { ms = JSON.parse(localStorage.getItem('ks.mySize')); no = sessionStorage.getItem('ks.noAuto') === '1'; } catch {}
    if (!ms || !ms.scale || !ms.size || no || /[?&]page=/.test(location.search)) return false;
    const q = `size:${ms.scale}:${ms.size} in:${plp.dataset.col}`;
    location.replace(`${plp.dataset.searchUrl}?q=${encodeURIComponent(q)}&type=product`);
    return true;
  }

  addEventListener('popstate', () => { if (document.querySelector('[data-ks-plp]')) load(location.href, false); });
  const boot = () => { const plp = document.querySelector('[data-ks-plp]'); if (plp && !plp.dataset.ready) { plp.dataset.ready = 1; if (!autoApply(plp)) wire(plp); } };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();
  document.addEventListener('shopify:section:load', boot);
})();
